import { v4 } from '@lukeed/uuid';
import { levels } from './levels';
/**
 * A FIFO logger that stores the last events in an array.
 */
export class FifoLogger {
    constructor(options = {}) {
        this.initialOptions = options;
        this.uuids = [v4()];
        this.events = [];
        this.level = options.level || 'info';
        this.levelAsNumber = levels.values[this.level];
        this.limit = options.limit ?? 1000;
        this.bindings = options.bindings ?? {};
        this.onChange = options.onChange;
    }
    setLevel(level) {
        this.level = level;
        this.levelAsNumber = levels.values[level];
    }
    setLimit(limit) {
        this.limit = limit;
        this.checkSize();
    }
    checkSize() {
        if (this.events.length > this.limit) {
            this.events.splice(0, this.events.length - this.limit);
        }
    }
    /**
     * Remove events from the current logger and its children.
     * @param options
     */
    clear() {
        for (let i = this.events.length - 1; i >= 0; i--) {
            if (this.events[i].uuids.includes(this.uuids[0])) {
                this.events.splice(i, 1);
            }
        }
        this.onChange?.(undefined, this.events, { depth: this.uuids.length });
    }
    getLogs(options = {}) {
        const { level, minLevel, includeChildren } = options;
        let logs = this.events.slice();
        if (includeChildren) {
            logs = logs.filter((log) => log.uuids.includes(this.uuids[0]));
        }
        else {
            logs = logs.filter((log) => log.uuids[0] === this.uuids[0]);
        }
        if (level) {
            const levelNumber = Number(levels.values[level]);
            if (Number.isNaN(levelNumber)) {
                throw new Error('Invalid level');
            }
            logs = logs.filter((log) => log.level === levelNumber);
        }
        if (minLevel) {
            const levelNumber = Number(levels.values[minLevel]);
            if (Number.isNaN(levelNumber)) {
                throw new Error('Invalid level');
            }
            logs = logs.filter((log) => log.level >= levelNumber);
        }
        return logs;
    }
    /**
     * @param bindings: an object of key-value pairs to include in log lines as properties.
     * @param options: an options object that will override child logger inherited options.
     */
    child(bindings) {
        const newFifoLogger = new FifoLogger(this.initialOptions);
        newFifoLogger.events = this.events;
        newFifoLogger.uuids = [v4(), ...this.uuids];
        newFifoLogger.bindings = { ...this.bindings, ...bindings };
        return newFifoLogger;
    }
    trace(value, message) {
        addEvent(this, levels.values.trace, value, message);
    }
    debug(value, message) {
        addEvent(this, levels.values.debug, value, message);
    }
    info(value, message) {
        addEvent(this, levels.values.info, value, message);
    }
    warn(value, message) {
        addEvent(this, levels.values.warn, value, message);
    }
    error(value, message) {
        addEvent(this, levels.values.error, value, message);
    }
    fatal(value, message) {
        addEvent(this, levels.values.fatal, value, message);
    }
}
function addEvent(logger, level, value, message) {
    if (level < logger.levelAsNumber)
        return;
    const event = {
        level,
        levelLabel: levels.labels[level],
        time: Date.now(),
        uuids: logger.uuids,
    };
    if (value instanceof Error) {
        event.message = value.toString();
        event.error = value;
        event.meta = { ...logger.bindings };
    }
    else if (message && typeof value === 'object') {
        event.message = message;
        event.meta = { ...logger.bindings, ...value };
    }
    else if (!message && typeof value === 'string') {
        event.message = value;
        event.meta = { ...logger.bindings };
    }
    else {
        throw new Error('Invalid arguments');
    }
    logger.events.push(event);
    logger.checkSize();
    if (logger.onChange) {
        logger.onChange(event, logger.events, { depth: logger.uuids.length });
    }
}
//# sourceMappingURL=FifoLogger.js.map